import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileCompletionBanner = ({ user }) => {
  const navigate = useNavigate();
  const [isHardBlock, setIsHardBlock] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [profileCompleteness, setProfileCompleteness] = useState(null);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (user?.profile_completion_required_at) {
      const requiredDate = new Date(user.profile_completion_required_at);
      const now = new Date();
      const diffTime = requiredDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      setDaysRemaining(diffDays);
      setIsHardBlock(diffDays <= 0);
    }
    
    // Fetch actual profile completeness from backend
    const fetchCompleteness = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/users/profile-completeness`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfileCompleteness(response.data.completeness);
        setIsComplete(response.data.is_complete || response.data.completeness >= 80);
      } catch (error) {
        // If endpoint fails, fall back to user.profile_completed
        setIsComplete(user?.profile_completed);
      }
    };
    
    fetchCompleteness();
  }, [user]);

  // Don't show if profile is completed (80%+ or profile_completed flag)
  if (isComplete || user?.profile_completed) {
    return null;
  }

  const getMessage = () => {
    if (user?.role === 'project_manager') {
      return isHardBlock 
        ? "⚠️ Complete your profile to create projects"
        : "💡 Complete your profile to unlock all features";
    } else if (user?.role === 'resource_manager' || user?.role === 'operating_partner') {
      return isHardBlock
        ? "⚠️ Complete your profile to publish resources"
        : "💡 Complete your profile to unlock all features";
    }
    return isHardBlock
      ? "⚠️ Complete your profile to access all features"
      : "💡 Complete your profile to unlock all features";
  };

  return (
    <>
      <div className={`${isHardBlock ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'} border-l-4 p-4 mb-6 rounded`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <AlertCircle className={`w-5 h-5 ${isHardBlock ? 'text-red-600' : 'text-blue-600'} mr-3`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${isHardBlock ? 'text-red-800' : 'text-blue-800'}`}>
                {getMessage()}
                {profileCompleteness !== null && (
                  <span className="ml-2 text-xs opacity-75">({profileCompleteness}% complete)</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2 ml-4">
            <Button
              size="sm"
              onClick={() => navigate('/profile')}
              style={{ backgroundColor: '#1B3240', color: 'white' }}
            >
              Complete Personal Info
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigate('/profile?tab=company');
              }}
            >
              Complete Company Info
            </Button>
          </div>
        </div>
      </div>
      
      {/* Visual separator */}
      <div className="border-t border-gray-200 mb-6"></div>
    </>
  );
};

export default ProfileCompletionBanner;
